import { Injectable, inject } from '@angular/core';
import { AbstractManageEntityFacade, DateService } from '@pf/shared-services';
import {
    BuildChangeTrackingFacadeFactory,
    findCustomField,
} from '@pf/shared/util-platform';
import {
    EntitySaveOptions,
    IManageEntityRoutes,
    SafeAny,
} from '@pf/shared-common';
import { map, Observable } from 'rxjs';
import { asArraySafe, dateRangeOverlaps } from '@pf/shared-utility';
import { SchnFunctionsDataService } from '../infrastructure/schn-functions-data.service';
import {
    ItemChargeDto,
    LaneRateCreate,
    LaneRateDataService,
    LaneRateDto,
    LaneRateMapper,
    LaneRateRead,
    LaneRateSearchParams,
    LaneRateStore,
} from '@pf/platform-loads/domain';
import { RateViewModelCreateBody } from '@control-tower/platform-loads';

export const requestedCommodityCfKey = 'Requested Commodity';

export function parseRequestedCommodityCustomField(laneRate: LaneRateDto) {
    return (
        findCustomField(
            asArraySafe(laneRate.customFields),
            requestedCommodityCfKey
        )?.value || ''
    ).split('|');
}

@Injectable()
export class LaneRateFacade extends AbstractManageEntityFacade<
    LaneRateDto,
    LaneRateRead,
    LaneRateCreate,
    LaneRateSearchParams
> {
    protected laneDataService: LaneRateDataService;
    private functionsService = inject(SchnFunctionsDataService);
    private dateService = inject(DateService);

    nameField: keyof LaneRateDto = 'name';

    constructor() {
        const laneDataService = inject(LaneRateDataService);
        const store = inject(LaneRateStore);
        const mapper = inject(LaneRateMapper);

        super(
            laneDataService,
            null as unknown as IManageEntityRoutes,
            store,
            mapper
        );

        this.laneDataService = laneDataService;
    }

    override add() {
        this.store.resetActiveEntity();
    }

    override manage() {
        this.store.resetActiveEntity();
    }

    override edit(entityId: string): void {
        this.store.setActiveEntity(entityId);
    }

    override save$(
        dto: LaneRateDto,
        options?: EntitySaveOptions<LaneRateDto>
    ): Observable<LaneRateDto> {
        return super.save$(dto, {
            ...(options || {}),
            postSave: savedDto => {
                savedDto.laneId = dto.laneId;
            },
        });
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        'name'
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof LaneRateDto, string>> {
        return { name: searchText };
    }

    checkIfLaneRateExists(laneRate: LaneRateDto) {
        return this.oneTimeSearch$({
            pageSize: 25,
            pageIndex: 1,
            sortField: 'createdDate',
            sortOrder: 'descend',
            filters: {
                laneId: laneRate.laneId,
            },
        }).pipe(
            map(result => {
                if (result.totalRecords === 0) {
                    return false;
                }
                const otherRates = result.data.filter(
                    rate => rate.id !== laneRate.id
                );
                const newRateStart = laneRate.startDate
                    ? (this.dateService.parseServerDateOnly(
                          laneRate.startDate as string
                      ) as Date)
                    : 1;
                const newRateEnd = laneRate.endDate
                    ? (this.dateService.parseServerDateOnly(
                          laneRate.endDate as string
                      ) as Date)
                    : 1;
                return otherRates.some(rate => {
                    // Check if rate dates overlap provided rate
                    const rateStart = rate.startDate
                        ? (this.dateService.parseServerDateOnly(
                              rate.startDate as string
                          ) as Date)
                        : 1;
                    const rateEnd = rate.endDate
                        ? (this.dateService.parseServerDateOnly(
                              rate.endDate as string
                          ) as Date)
                        : 1;

                    return (
                        this.commodityMatches(rate, laneRate) &&
                        dateRangeOverlaps(
                            newRateStart,
                            newRateEnd,
                            rateStart,
                            rateEnd
                        )
                    );
                });
            })
        );
    }

    commodityMatches(existingRate: LaneRateDto, newRate: LaneRateDto): boolean {
        const existingCommodityId = existingRate.description;
        const newCommodityId = newRate.description;

        const existingRequestedCommodityId =
            this.getRequestedCommodityId(existingRate);
        const newRequestedCommodityId = this.getRequestedCommodityId(newRate);

        return (
            existingCommodityId == newCommodityId ||
            (!!newRequestedCommodityId &&
                existingCommodityId == newRequestedCommodityId) ||
            (!!existingRequestedCommodityId &&
                existingRequestedCommodityId == newRequestedCommodityId)
        );
    }

    getRequestedCommodityId(laneRate: LaneRateDto) {
        return parseRequestedCommodityCustomField(laneRate)?.[1];
    }

    addItemCharge(charge: ItemChargeDto) {
        // Marcus - Bit of a hack, but we need to charges in the local store for duplicate checks before a server refresh
        const entity = this.store.get(charge.rateId as string);
        if (entity) {
            const charges = entity.itemCharges || [];
            charges.push(charge as SafeAny);
            entity.itemCharges = charges;
            this.store.update(entity);
        }
    }

    update(rate: LaneRateDto) {
        this.store.update(this.mapper.toEntity(rate));
    }

    runAutomationsForEntity$(laneId: string, rateId: string) {
        return this.functionsService.runAutomationsForRate$(laneId, rateId);
    }

    override applyResourceAuth(body: RateViewModelCreateBody): void {
        body.resourceAuth = { allAuthorized: true };
    }
}
